import React from 'react';
import { LocateFixedIcon, MinusIcon, NavigationIcon, PlusIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import type { Vessel } from '../data/operations';
type WorldMapProps = {
  vessels: Vessel[];
  selectedId: string | null;
  onSelect: (id: string) => void;
};
const land = ['M40 83 L72 61 120 57 151 72 146 91 127 98 117 126 97 137 80 119 60 112 44 96Z', 'M132 143 L160 151 171 180 162 221 144 249 128 220 135 185 122 160Z', 'M318 61 L354 53 386 64 400 80 430 82 446 102 429 118 397 113 378 127 344 118 328 96 305 87Z', 'M389 124 L423 126 445 148 439 190 419 226 391 213 380 180 382 148Z', 'M447 80 L498 62 562 69 613 87 665 83 714 103 733 128 695 143 651 131 620 151 577 140 543 122 500 128 467 111Z', 'M638 183 L683 174 721 193 711 222 670 231 641 214Z', 'M748 209 L783 202 806 218 790 235 753 232 738 219Z'];
const routePaths = [{
  d: 'M612 181 Q495 92 364 95',
  tone: '#45e7f2'
}, {
  d: 'M702 119 Q790 42 108 111',
  tone: '#a3e635'
}, {
  d: 'M367 96 Q253 51 136 115',
  tone: '#45e7f2'
}, {
  d: 'M544 144 Q616 196 674 190',
  tone: '#f59e0b'
}, {
  d: 'M105 112 Q42 32 716 111',
  tone: '#45e7f2'
}];
export function WorldMap({
  vessels,
  selectedId,
  onSelect
}: WorldMapProps) {
  const reduceMotion = useReducedMotion();
  const selected = vessels.find(vessel => vessel.id === selectedId);
  return <section className="relative min-h-[390px] overflow-hidden border border-slate-700/80 bg-[#07121a]" aria-labelledby="map-heading" data-id="element-161">
      <div className="absolute left-0 right-0 top-0 z-20 flex items-center justify-between border-b border-slate-700/70 bg-[#09151e]/95 px-3 py-2.5" data-id="element-162">
        <div data-id="element-163">
          <h2 id="map-heading" className="font-display text-xs font-bold tracking-[0.16em] text-slate-100" data-id="element-164">
            GLOBAL VESSEL NETWORK
          </h2>
          <p className="mt-0.5 font-mono text-[9px] text-slate-500" data-id="element-165">
            AIS TELEMETRY · REFRESH 4.2 SEC · 248 CONTACTS
          </p>
        </div>
        <div className="hidden items-center gap-3 font-mono text-[9px] text-slate-400 sm:flex" aria-label="Map legend" data-id="element-166">
          <span className="flex items-center gap-1.5" data-id="element-167">
            <i className="h-1.5 w-1.5 rounded-full bg-lime-400" data-id="element-168" /> NOMINAL
          </span>
          <span className="flex items-center gap-1.5" data-id="element-169">
            <i className="h-1.5 w-1.5 rounded-full bg-amber-400" data-id="element-170" /> WATCH
          </span>
          <span className="flex items-center gap-1.5" data-id="element-171">
            <i className="h-1.5 w-1.5 rounded-full bg-red-400" data-id="element-172" /> CRITICAL
          </span>
        </div>
      </div>

      <svg viewBox="0 0 840 300" className="absolute inset-0 mt-12 h-[calc(100%-3rem)] w-full" role="img" aria-label="World map showing major shipping routes and vessel positions" preserveAspectRatio="xMidYMid meet" data-id="element-173">
        <defs data-id="element-174">
          <pattern id="grid" width="28" height="28" patternUnits="userSpaceOnUse" data-id="element-175">
            <path d="M 28 0 L 0 0 0 28" fill="none" stroke="#17313d" strokeWidth="0.55" data-id="element-176" />
          </pattern>
          <filter id="glow" data-id="element-177">
            <feGaussianBlur stdDeviation="2" result="blur" data-id="element-178" />
            <feMerge data-id="element-179">
              <feMergeNode in="blur" data-id="element-180" />
              <feMergeNode in="SourceGraphic" data-id="element-181" />
            </feMerge>
          </filter>
        </defs>
        <rect width="840" height="300" fill="url(#grid)" data-id="element-182" />
        <g fill="#102632" stroke="#285060" strokeWidth="0.7" data-id="element-183">
          {land.map(path => <path key={path} d={path} data-id="element-184" />)}
        </g>
        <g fill="none" strokeDasharray="4 5" opacity="0.8" data-id="element-185">
          {routePaths.map((route, index) => <motion.path key={route.d} d={route.d} stroke={route.tone} strokeWidth="1.1" initial={reduceMotion ? false : {
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 0.75
        }} transition={{
          duration: 1.2,
          delay: index * 0.12,
          ease: 'easeOut'
        }} data-id="element-186" />)}
        </g>
        {[{
        x: 109,
        y: 112,
        r: 15,
        tone: '#ef4444'
      }, {
        x: 614,
        y: 180,
        r: 12,
        tone: '#f59e0b'
      }, {
        x: 365,
        y: 94,
        r: 9,
        tone: '#f59e0b'
      }].map(spot => <g key={`${spot.x}-${spot.y}`} data-id="element-187">
            <circle cx={spot.x} cy={spot.y} r={spot.r} fill="none" stroke={spot.tone} strokeWidth="0.7" opacity="0.35" data-id="element-188" />
            <circle cx={spot.x} cy={spot.y} r="3" fill={spot.tone} opacity="0.8" filter="url(#glow)" data-id="element-189" />
          </g>)}
      </svg>

      <div className="absolute inset-0 mt-12" aria-label="Vessel markers" data-id="element-190">
        {vessels.map(vessel => {
        const active = vessel.id === selectedId;
        const tone = vessel.status === 'delayed' ? 'text-red-300 border-red-400 bg-red-400' : vessel.status === 'anchored' ? 'text-amber-300 border-amber-400 bg-amber-400' : 'text-cyan-200 border-cyan-400 bg-cyan-400';
        return <button key={vessel.id} onClick={() => onSelect(vessel.id)} className={`group absolute -translate-x-1/2 -translate-y-1/2 rounded-full focus:outline-none focus:ring-2 focus:ring-white ${tone.split(' ').slice(0, 2).join(' ')}`} style={{
          left: `${vessel.x}%`,
          top: `${vessel.y}%`
        }} aria-label={`${vessel.name}, ${vessel.route}, ${vessel.status}`} aria-pressed={active} data-id="element-191">
              <span className={`absolute -inset-2 rounded-full border opacity-40 ${active ? 'animate-ping motion-reduce:animate-none' : ''}`} data-id="element-192" />
              <span className={`grid h-5 w-5 place-items-center rounded-full border bg-[#07121a] ${active ? 'scale-125' : ''}`} data-id="element-193">
                <NavigationIcon className="h-2.5 w-2.5 fill-current" style={{
              transform: `rotate(${vessel.heading}deg)`
            }} data-id="element-194" />
              </span>
            </button>;
      })}
      </div>

      <div className="absolute bottom-3 left-3 z-10 flex flex-col border border-slate-700 bg-[#08131c]/95" data-id="element-195">
        {[PlusIcon, MinusIcon, LocateFixedIcon].map((Icon, index) => <button key={index} className="grid h-8 w-8 place-items-center border-b border-slate-700 text-slate-400 last:border-b-0 hover:bg-slate-800 hover:text-cyan-300 focus:outline-none focus:ring-1 focus:ring-cyan-400" aria-label={index === 0 ? 'Zoom in' : index === 1 ? 'Zoom out' : 'Center map'} data-id="element-196">
            <Icon className="h-3.5 w-3.5" data-id="element-197" />
          </button>)}
      </div>

      {selected && <motion.div initial={reduceMotion ? false : {
      opacity: 0,
      x: 12
    }} animate={{
      opacity: 1,
      x: 0
    }} className="absolute bottom-3 right-3 z-10 w-56 border border-cyan-400/50 bg-[#07121a]/95 p-3 shadow-[0_0_20px_rgba(34,211,238,0.08)]" role="status" data-id="element-198">
          <div className="flex items-center justify-between" data-id="element-199">
            <p className="font-mono text-[9px] tracking-widest text-cyan-400" data-id="element-200">
              VESSEL SELECTED
            </p>
            <span className="font-mono text-[8px] uppercase text-lime-300" data-id="element-201">
              {selected.status}
            </span>
          </div>
          <p className="mt-1 text-sm font-semibold text-white" data-id="element-202">
            {selected.name}
          </p>
          <p className="mt-1 font-mono text-[10px] text-slate-400" data-id="element-203">
            {selected.route} · {selected.speed}
          </p>
          <p className="mt-1 font-mono text-[9px] text-slate-500" data-id="element-204">
            ETA {selected.eta}
          </p>
        </motion.div>}

      <div className="absolute bottom-3 left-14 hidden font-mono text-[8px] tracking-widest text-slate-600 sm:block" data-id="element-205">
        PROJECTION / MERCATOR · LAT 14.4°N LON 28.1°E
      </div>
    </section>;
}